// The single forum view for every role. One page, permission-layered:
//   - Members/advisors see their forum's identity, schedule, meetings, and roster.
//   - Facilitators of the selected forum (roster.can_manage) also get the
//     Message Forum modal (chat + announcement), schedule editing, and the
//     operational dashboard (Forum Roster, health charts).
//   - Admins (and god_mode) additionally get an all-forums switcher, Create
//     Forum, pricing, and facilitator assignment.
// This page replaces the old My Forum hub, Forum Roster, and Forum Management
// console, which duplicated each other's member/invite/edit features.

import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users,
  User as UserIcon,
  MessageSquare,
  Calendar,
  CalendarDays,
  CalendarClock,
  ChevronDown,
  Clock,
  MapPin,
  CalendarOff,
  Pencil,
  Plus,
  Search,
  X,
  Building2,
  FileText,
  Target,
  ChevronRight,
  History,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { accountabilityGroupsAPI, forumsAPI, eventsAPI, usersAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { hasGuestPrivileges, isAdminRole, isFacilitatorRole, isLeadershipRole, publicRoleLabel } from '../utils/roles';
import { useVisibilityPolling } from '../hooks/useVisibilityPolling';
import { useCalendarConnected } from '../hooks/useCalendarConnected';
import EventCalendarAction, { ConnectCalendarHint } from '../components/EventCalendarAction';
import EventRsvpButtons from '../components/EventRsvpButtons';
import { AddressLink } from '../components/OpenInMapsModal';
import MessageForumModal from '../components/MessageForumModal';
import FacilitatorDashboardPanel from './FacilitatorDashboardPanel';
import ConfirmModal from '../components/ConfirmModal';
import ForumFormModal, { type FacilitatorOption } from '../components/ForumFormModal';
import InviteMemberModal from '../components/InviteMemberModal';
import type {
  ForumListItem,
  ForumRoster,
  RosterMember,
  UpcomingEvent,
  ForumSchedule,
  ForumDetail,
  UserListItem,
  PaymentTrackingRow,
} from '../types';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const ORDINALS: { value: number; label: string }[] = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: -1, label: 'Last' },
];

function scheduleSummary(s: ForumSchedule): string {
  const day = WEEKDAYS[s.weekday] ?? 'day';
  const time = s.start_time?.slice(0, 5) ?? '';
  if (s.frequency === 'weekly') return `Every ${day} at ${time}`;
  const ord = ORDINALS.find((o) => o.value === s.ordinal)?.label ?? '';
  return `${ord} ${day} of each month at ${time}`;
}

// Render a DRF decimal string ("400.00") as a clean dollar amount ("$400").
function formatForumPrice(price: string | undefined): string {
  const amount = Number(price);
  if (!price || Number.isNaN(amount)) return '$0';
  return Number.isInteger(amount)
    ? `$${amount.toLocaleString()}`
    : `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function MemberCard({
  member,
  onSelect,
  interactive = true,
}: {
  member: RosterMember;
  onSelect?: (id: number) => void;
  interactive?: boolean;
}) {
  const location = [member.location_city, member.location_state].filter(Boolean).join(', ');
  const inner = (
    <>
      <div className="w-11 h-11 bg-off-white rounded-full flex items-center justify-center flex-shrink-0">
        {member.avatar_url ? (
          <img src={member.avatar_url} alt={member.full_name} className="w-11 h-11 rounded-full object-cover" />
        ) : (
          <UserIcon className="text-navy" size={20} />
        )}
      </div>
      <div className="min-w-0 text-left">
        <h4 className="font-medium text-navy truncate">{member.full_name}</h4>
        <p className="text-sm text-gray-500 truncate">
          {[member.industry, location].filter(Boolean).join(' • ') || publicRoleLabel(member.role)}
        </p>
      </div>
    </>
  );
  const className =
    'bg-white rounded-xl p-4 border border-gray-100 hover:border-gold transition flex items-center gap-3 w-full';
  if (!interactive) {
    return <div className={className}>{inner}</div>;
  }
  // Facilitators open the forum-scoped operational page; everyone else opens
  // the visibility-aware public member profile.
  if (onSelect) {
    return (
      <button type="button" onClick={() => onSelect(member.id)} className={className}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={`/members/${member.id}`} className={className}>
      {inner}
    </Link>
  );
}

interface QuickLink {
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  badge?: number;
}

function QuickLinkCard({ link }: { link: QuickLink }) {
  const Icon = link.icon;
  return (
    <Link
      to={link.to}
      className="group flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm hover:shadow-md hover:ring-1 hover:ring-navy/10 transition"
    >
      <div className="w-10 h-10 rounded-lg bg-off-white flex items-center justify-center flex-shrink-0 text-navy group-hover:bg-navy group-hover:text-white transition-colors">
        <Icon size={19} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-navy text-sm">{link.label}</h3>
          {link.badge ? (
            <span className="bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
              {link.badge}
            </span>
          ) : null}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{link.description}</p>
      </div>
      <ChevronRight size={16} className="text-gray-300 group-hover:text-navy transition-colors flex-shrink-0" />
    </Link>
  );
}


export default function Forum() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const isGuest = hasGuestPrivileges(user);
  const { connected: calendarConnected, loading: calendarLoading } = useCalendarConnected();
  const [myForums, setMyForums] = useState<ForumListItem[]>([]);
  // Admins can open any forum, not just their own.
  const [otherForums, setOtherForums] = useState<ForumListItem[]>([]);
  const [selectedForumId, setSelectedForumId] = useState<number | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [roster, setRoster] = useState<ForumRoster | null>(null);
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [schedules, setSchedules] = useState<ForumSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [payments, setPayments] = useState<PaymentTrackingRow[]>([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  // Header card's collapsible "Upcoming meetings" list (the old Meetings card).
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showPastForums, setShowPastForums] = useState(false);
  const [accountabilityPending, setAccountabilityPending] = useState(0);
  // Per-occurrence override state: reschedule/skip ONE meeting without
  // touching the recurring schedule.
  const [skippingOccurrence, setSkippingOccurrence] = useState<UpcomingEvent | null>(null);
  const [showScheduleScope, setShowScheduleScope] = useState(false);
  const [scheduleScope, setScheduleScope] = useState<'temporary' | 'recurring' | null>(null);
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | null>(null);
  // Forum-management state (facilitators of this forum + admins).
  const [forumDetail, setForumDetail] = useState<ForumDetail | null>(null);
  const [showForumModal, setShowForumModal] = useState(false);
  const [forumModalMode, setForumModalMode] = useState<'create' | 'edit'>('edit');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [showAddFacilitator, setShowAddFacilitator] = useState(false);
  const [directory, setDirectory] = useState<UserListItem[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [removingMember, setRemovingMember] = useState<RosterMember | null>(null);
  const [removingFacilitator, setRemovingFacilitator] = useState<RosterMember | null>(null);

  useVisibilityPolling({
    fetcher: accountabilityGroupsAPI.pendingInvitations,
    onSuccess: (pending) => {
      setAccountabilityPending(pending.to_approve.length + pending.to_accept.length);
    },
    intervalMs: 8000,
    onError: (error) => console.error('Error loading forum hub counts:', error),
  });

  const loadForumLists = useCallback(async () => {
    const mine = await forumsAPI.getMyForums();
    setMyForums(mine);
    let others: ForumListItem[] = [];
    if (isAdmin) {
      // Let failures propagate: silently treating "couldn't load the org's
      // forums" as "there are no forums" rendered the misleading "you're not
      // in a Forum yet" state whenever a request transiently failed.
      const all = await forumsAPI.getAll();
      const mineIds = new Set(mine.map((f) => f.id));
      others = all.filter((f) => !mineIds.has(f.id));
    }
    setOtherForums(others);
    return [...mine, ...others];
  }, [isAdmin]);

  // Load the forum lists once (and on demand via Retry) — NOT on every forum
  // switch. The picker updates ?forum=, and refetching both lists per click
  // hammered the API for no benefit.
  const initialLoad = useCallback(async () => {
    setLoadError(false);
    try {
      const forums = await loadForumLists();
      if (forums.length > 0) {
        const requestedId = Number(searchParams.get('forum'));
        const requested = forums.find((forum) => forum.id === requestedId);
        setSelectedForumId((prev) => requested?.id ?? prev ?? forums[0].id);
      }
    } catch {
      setLoadError(true);
      toast.error('Could not load your Forums');
    } finally {
      setIsLoading(false);
    }
    // searchParams is only read for the initial ?forum= deep link; URL changes
    // afterwards are handled by the sync effect below without refetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadForumLists]);

  useEffect(() => {
    initialLoad();
  }, [initialLoad]);

  // Follow ?forum= changes (back/forward, in-app links) using the lists we
  // already have.
  useEffect(() => {
    const requestedId = Number(searchParams.get('forum'));
    if (!requestedId) return;
    if ([...myForums, ...otherForums].some((f) => f.id === requestedId)) {
      setSelectedForumId(requestedId);
    }
  }, [searchParams, myForums, otherForums]);

  const loadForum = useCallback(async (forumId: number) => {
    try {
      const [rosterData, eventsData, scheduleData] = await Promise.all([
        eventsAPI.getRoster(forumId),
        eventsAPI.getUpcoming({ forum: forumId, days: 60, past_days: 180 }),
        eventsAPI.getSchedules(forumId).catch(() => [] as ForumSchedule[]),
      ]);
      setRoster(rosterData);
      setEvents(eventsData);
      setSchedules(scheduleData);
      // Facilitators/admins also get per-member payment tracking and the full
      // forum detail (needed for the edit-forum form and pricing display).
      if (rosterData.can_manage) {
        try {
          const tracking = await forumsAPI.getPaymentTracking(forumId);
          setPayments(tracking.members);
        } catch {
          setPayments([]);
        }
        forumsAPI.getById(forumId).then(setForumDetail).catch(() => setForumDetail(null));
      } else {
        setPayments([]);
        setForumDetail(null);
      }
    } catch {
      toast.error('Could not load the Forum roster');
    }
  }, []);

  useEffect(() => {
    if (selectedForumId) loadForum(selectedForumId);
  }, [selectedForumId, loadForum]);

  // Directory powers the "Add existing member" / "Add facilitator" pickers and
  // the facilitator candidates in the edit-forum form (managers only).
  useEffect(() => {
    if (!roster?.can_manage) return;
    usersAPI.getDirectory().then(setDirectory).catch(() => setDirectory([]));
  }, [roster?.can_manage]);

  const facilitatorCandidates: FacilitatorOption[] = useMemo(() => {
    const map = new Map<number, FacilitatorOption>();
    directory
      .filter((u) => isLeadershipRole(u.role))
      .forEach((u) => map.set(u.id, { id: u.id, full_name: u.full_name, role: u.role }));
    if (user && isLeadershipRole(user.role)) {
      const name = user.full_name || `${user.first_name} ${user.last_name}`.trim() || user.email;
      map.set(user.id, { id: user.id, full_name: `${name} (you)`, role: user.role });
    }
    forumDetail?.facilitators.forEach((f) => {
      if (!map.has(f.id)) map.set(f.id, { id: f.id, full_name: f.full_name, role: f.role });
    });
    return Array.from(map.values()).sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [directory, user, forumDetail]);

  const scheduleOccurrences = useMemo(() => {
    const now = new Date();
    const endOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    return events.filter((event) => {
      const startsAt = new Date(event.starts_at);
      return (
        event.schedule !== null &&
        startsAt >= now &&
        startsAt < endOfNextMonth
      );
    });
  }, [events]);

  const handleSelectForum = (forumId: number) => {
    setSelectedForumId(forumId);
    // Preserve any other params across forum switches.
    const next = new URLSearchParams(searchParams);
    next.set('forum', String(forumId));
    setSearchParams(next);
  };

  const canManage = roster?.can_manage ?? false;

  // Facilitators get the chat-vs-announcement modal; members go straight to
  // the group chat (a one-option modal would just be a speed bump).
  const handleMessageForum = () => {
    if (!roster?.default_thread_id) {
      toast.error('This Forum has no group chat yet.');
      return;
    }
    if (canManage) {
      setShowMessageModal(true);
    } else {
      navigate(`/messages?thread=${roster.default_thread_id}`);
    }
  };

  // Skip ONE occurrence: a computed occurrence gets a cancelled override row;
  // an already-persisted event is just marked cancelled. The recurring
  // schedule is untouched, so the following months are unaffected.
  const handleSkipOccurrence = async () => {
    const event = skippingOccurrence;
    if (!event) return;
    setSkippingOccurrence(null);
    try {
      if (event.id) {
        await eventsAPI.updateEvent(event.id, { status: 'cancelled' });
      } else {
        await eventsAPI.createEvent({
          forum_id: event.forum,
          schedule: event.schedule,
          is_override: true,
          occurrence_date: event.occurrence_date,
          title: event.title,
          starts_at: event.starts_at,
          ends_at: event.ends_at,
          location: event.location,
          description: event.description,
          status: 'cancelled',
        });
      }
      toast.success('That meeting was cancelled — the regular schedule is unchanged');
      if (selectedForumId) loadForum(selectedForumId);
    } catch {
      toast.error('Could not cancel that meeting');
    }
  };

  const handleForumSaved = async (message: string, forumId: number) => {
    toast.success(message);
    // The forum name/schedule may have changed (or a new forum was created);
    // refresh the picker and open the affected forum.
    try {
      await loadForumLists();
    } catch {
      // Non-fatal: the page still shows the saved forum below.
    }
    if (forumModalMode === 'create') {
      handleSelectForum(forumId);
    } else {
      loadForum(forumId);
    }
  };

  const handleAddExisting = async (userId: number) => {
    if (!selectedForumId) return;
    try {
      const result = await forumsAPI.addMember(selectedForumId, userId);
      if (result.warning) toast.error(result.warning);
      toast.success(result.message);
      setShowAddExisting(false);
      setMemberSearch('');
      loadForum(selectedForumId);
    } catch {
      toast.error('Could not add that member');
    }
  };

  const handleRemoveMember = async () => {
    const member = removingMember;
    setRemovingMember(null);
    if (!member || !selectedForumId) return;
    try {
      const result = await forumsAPI.removeMember(selectedForumId, member.id);
      toast.success(result.message);
      loadForum(selectedForumId);
    } catch {
      toast.error('Could not remove that member');
    }
  };

  // Facilitator assignment is admin-only (the backend enforces this too).
  const handleAddFacilitator = async (userId: number) => {
    if (!selectedForumId) return;
    try {
      const result = await forumsAPI.addFacilitator(selectedForumId, userId);
      toast.success(result.message);
      setShowAddFacilitator(false);
      setMemberSearch('');
      loadForum(selectedForumId);
    } catch {
      toast.error('Could not add that facilitator');
    }
  };

  const handleRemoveFacilitator = async () => {
    const facilitator = removingFacilitator;
    setRemovingFacilitator(null);
    if (!facilitator || !selectedForumId) return;
    try {
      const result = await forumsAPI.removeFacilitator(selectedForumId, facilitator.id);
      toast.success(result.message);
      loadForum(selectedForumId);
    } catch {
      toast.error('Could not remove that facilitator');
    }
  };

  const openCreateForum = () => {
    setForumModalMode('create');
    setShowForumModal(true);
  };

  const openEditForum = () => {
    setForumModalMode('edit');
    setShowForumModal(true);
  };

  const quickLinks: QuickLink[] = [
    {
      to: '/meetings',
      label: '1:1 Meetings',
      description: 'Request and manage one-on-one meetings.',
      icon: CalendarClock,
    },
    {
      to: '/members',
      label: 'Member Directory',
      description: 'Browse the full Iron Forums network.',
      icon: Users,
    },
    {
      to: '/snapshots?tab=group',
      label: 'Snapshot Group',
      description: "This month's snapshot-sharing group.",
      icon: FileText,
    },
    {
      to: '/accountability-groups',
      label: 'Accountability Groups',
      description: 'Your circle for prayers, goals, and snapshots.',
      icon: Target,
      badge: accountabilityPending || undefined,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  const forums = [...myForums, ...otherForums];
  const nowMs = Date.now();
  const forumEvents = events.filter((e) => e.kind !== 'member_meeting');
  const upcomingEvents = forumEvents.filter((e) => new Date(e.starts_at).getTime() >= nowMs);
  const pastEvents = forumEvents
    .filter((e) => new Date(e.starts_at).getTime() < nowMs)
    .slice()
    .reverse();
  const nextMeeting = upcomingEvents[0] ?? null;
  const meetingDate = nextMeeting ? new Date(nextMeeting.starts_at) : null;
  const memberCount = roster
    ? new Set([...roster.facilitators, ...roster.members].map((m) => m.id)).size
    : 0;
  const facilitatorNames = roster?.facilitators
    .map((f) => f.full_name)
    .filter(Boolean)
    .join(', ');

  // A failed load is NOT "you have no forums" — show an explicit error with
  // a retry instead of the misleading membership explainer.
  if (forums.length === 0 && loadError) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
        <Users className="mx-auto text-gray-300" size={48} />
        <p className="text-gray-500 mt-2">Couldn't load your Forums.</p>
        <button
          onClick={() => {
            setIsLoading(true);
            initialLoad();
          }}
          className="mt-4 inline-flex items-center gap-2 bg-navy text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-navy-dark transition"
        >
          Try again
        </button>
      </div>
    );
  }

  // No forum at all: members get an explainer; facilitators can self-serve
  // their own forum; admins can create one for the org.
  if (forums.length === 0) {
    return (
      <div className="space-y-8">
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
          <Users className="mx-auto text-gray-300" size={48} />
          <p className="text-gray-500 mt-2">You're not part of a Forum yet.</p>
          {(isFacilitatorRole(user?.role) || isAdmin) && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-3">
                Set up the Forum you'll lead — name, meeting location, and
                schedule — then start inviting members.
              </p>
              <button
                onClick={openCreateForum}
                className="inline-flex items-center gap-2 bg-navy text-white px-4 py-2.5 rounded-lg font-semibold hover:bg-navy-dark transition"
              >
                <Building2 size={17} />
                Create Your Forum
              </button>
            </div>
          )}
        </div>

        <section>
          <h2 className="text-lg font-semibold text-navy mb-3">Quick links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {quickLinks.map((link) => (
              <QuickLinkCard key={link.to} link={link} />
            ))}
          </div>
        </section>

        {user && (
          <ForumFormModal
            isOpen={showForumModal}
            mode="create"
            forum={null}
            facilitatorCandidates={facilitatorCandidates}
            lockedFacilitator={
              isAdmin
                ? undefined
                : {
                    id: user.id,
                    full_name: `${user.first_name} ${user.last_name}`.trim() || user.username,
                    role: user.role,
                  }
            }
            onClose={() => setShowForumModal(false)}
            onSaved={handleForumSaved}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Forum identity header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              {myForums.length > 1 || otherForums.length > 0 ? 'My Forums' : 'My Forum'}
            </p>
            <h1 className="text-2xl font-bold text-navy mt-0.5">
              {roster?.forum.name ?? 'Forum'}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mt-1.5">
              {(roster?.forum.city || roster?.forum.state) && (
                <span className="flex items-center gap-1.5">
                  <MapPin size={15} className="text-gray-400" />
                  {[roster?.forum.city, roster?.forum.state].filter(Boolean).join(', ')}
                </span>
              )}
              {!isGuest && (
                <span className="flex items-center gap-1.5">
                  <Users size={15} className="text-gray-400" />
                  {memberCount} members
                </span>
              )}
              {facilitatorNames && <span>Facilitated by {facilitatorNames}</span>}
            </div>
            {roster?.can_manage && forumDetail && (
              <p className="text-sm font-medium text-gray-700 mt-1.5">
                Members {formatForumPrice(forumDetail.monthly_price)} / month
                <span className="text-gray-400"> · </span>
                Advisors {formatForumPrice(forumDetail.advisor_monthly_price)} / month
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            {forums.length > 1 && (
              <select
                value={selectedForumId ?? ''}
                onChange={(e) => handleSelectForum(Number(e.target.value))}
                aria-label="Switch Forum"
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                {myForums.length > 0 && otherForums.length > 0 ? (
                  <>
                    <optgroup label="My Forums">
                      {myForums.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="All Forums">
                      {otherForums.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </optgroup>
                  </>
                ) : (
                  forums.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))
                )}
              </select>
            )}
            {roster?.can_manage && forumDetail && (
              <button
                onClick={openEditForum}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-navy text-navy rounded-lg text-sm font-medium hover:bg-navy/5 transition"
              >
                <Pencil size={15} />
                Edit Forum
              </button>
            )}
            {(isAdmin || isFacilitatorRole(user?.role)) && (
              <button
                onClick={openCreateForum}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-gold text-gold rounded-lg text-sm font-medium hover:bg-gold/5 transition"
              >
                <Plus size={15} />
                Create Forum
              </button>
            )}
            {roster?.default_thread_id && !isGuest && (
              <button
                onClick={handleMessageForum}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 w-full sm:w-auto bg-gold text-white rounded-lg text-sm font-medium hover:bg-gold-dark transition"
              >
                <MessageSquare size={16} />
                Message Forum
              </button>
            )}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
          <div className="flex items-start gap-1.5 text-sm">
            <CalendarDays size={15} className="text-gold-dark flex-shrink-0 mt-0.5" />
            {meetingDate ? (
              <span className="text-gray-700 min-w-0">
                Next meeting:{' '}
                <span className="font-medium text-navy">
                  {meetingDate.toLocaleDateString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}{' '}
                  at{' '}
                  {meetingDate.toLocaleTimeString([], {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
                {nextMeeting?.location && !isGuest && (
                  <span className="block mt-1.5">
                    <AddressLink
                      address={nextMeeting.location}
                      label={nextMeeting.venue_name || nextMeeting.forum_name || nextMeeting.title}
                    />
                  </span>
                )}
              </span>
            ) : (
              <span className="text-gray-500">
                {roster?.can_manage
                  ? 'No upcoming meetings — set the meeting schedule to start generating them.'
                  : 'No upcoming meetings scheduled.'}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isGuest && (upcomingEvents.length > 0 || pastEvents.length > 0) && (
              <button
                onClick={() => setShowUpcoming((v) => !v)}
                aria-expanded={showUpcoming}
                className="inline-flex items-center gap-1 text-navy font-medium hover:bg-off-white px-2 py-1 rounded-lg transition"
              >
                {showUpcoming ? 'Hide meetings' : `All meetings (${upcomingEvents.length})`}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${showUpcoming ? 'rotate-180' : ''}`}
                />
              </button>
            )}
            {roster?.can_manage && forumDetail && (
              <button
                onClick={() => {
                  if (schedules.some((schedule) => schedule.is_active)) {
                    setScheduleScope(null);
                    setSelectedOccurrenceId(null);
                    setShowScheduleScope(true);
                  } else {
                    openEditForum();
                  }
                }}
                className="inline-flex items-center gap-1.5 text-navy font-medium hover:bg-off-white px-2 py-1 rounded-lg transition"
              >
                <Pencil size={13} />
                {schedules.some((s) => s.is_active) ? 'Edit Schedule' : 'Set Schedule'}
              </button>
            )}
          </div>
        </div>

        {/* Collapsible meetings detail (the old Meetings card): recurring
            cadence plus the generated occurrences, with per-meeting cancel
            (facilitators) and add-to-calendar actions. */}
        {showUpcoming && (upcomingEvents.length > 0 || pastEvents.length > 0) && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            {schedules.filter((s) => s.is_active).length > 0 && (
              <ul className="space-y-1 text-sm text-gray-700 mb-3">
                {schedules
                  .filter((s) => s.is_active)
                  .map((s) => (
                    <li key={s.id} className="flex items-center gap-2">
                      <Clock size={14} className="text-gray-400" />
                      {scheduleSummary(s)} ({s.duration_minutes} min, {s.timezone})
                    </li>
                  ))}
              </ul>
            )}
            {upcomingEvents.length > 0 && (
            <>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Upcoming
              </h3>
              <ConnectCalendarHint connected={calendarConnected} loading={calendarLoading} />
            </div>
            {roster?.can_manage && (
              <p className="text-xs text-gray-400 mb-3">
                Use Edit Schedule above to reschedule one month or change the recurring pattern.
                Cancelling below affects only that meeting.
              </p>
            )}
            <div className="space-y-3 mt-2">
              {upcomingEvents.map((event) => (
              <div key={event.occurrence_id} className="p-3 bg-off-white rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-navy break-words">
                      {event.title}
                      {event.is_override && (
                        <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-gold/15 text-gold align-middle">
                          Rescheduled
                        </span>
                      )}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar size={13} />
                        {new Date(event.starts_at).toLocaleDateString(undefined, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={13} />
                        {new Date(event.starts_at).toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                      {event.location && (
                        <AddressLink
                          address={event.location}
                          label={event.venue_name || event.title}
                          className="basis-full"
                        />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {roster?.can_manage && (
                      <button
                        onClick={() => setSkippingOccurrence(event)}
                        title="Cancel this meeting only"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-white rounded-lg transition"
                      >
                        <CalendarOff size={14} />
                        Cancel
                      </button>
                    )}
                    <EventRsvpButtons event={event} />
                    <EventCalendarAction
                      icsUrl={event.ics_url}
                      links={event.calendar_links}
                      filename={`${event.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'event'}.ics`}
                      connected={calendarConnected}
                    />
                  </div>
                </div>
              </div>
              ))}
            </div>
            </>
            )}
            {pastEvents.length > 0 && (
              <div className="mt-5 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setShowPastForums((v) => !v)}
                  className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase tracking-wide hover:text-navy transition"
                >
                  <History size={14} />
                  Past forums
                  <span className="normal-case font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {pastEvents.length}
                  </span>
                  {showPastForums ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {showPastForums && (
                  <div className="space-y-2 mt-3">
                    {pastEvents.map((event) => (
                      <div key={event.occurrence_id} className="p-3 bg-off-white rounded-lg opacity-80">
                        <p className="font-medium text-navy break-words">{event.title}</p>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar size={13} />
                            {new Date(event.starts_at).toLocaleDateString(undefined, {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                          {event.location && (
                            <AddressLink
                              address={event.location}
                              label={event.venue_name || event.title}
                              className="basis-full"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Facilitators/admins: the operational dashboard (triage, stats,
          Forum Roster, health charts) replaces the old Overview/Health tabs. */}
      {canManage && selectedForumId != null && (
        <FacilitatorDashboardPanel
          forumId={selectedForumId}
          roster={roster}
          payments={payments}
          onInvite={() => setShowInviteModal(true)}
          onManageSaved={() => { if (selectedForumId) loadForum(selectedForumId); }}
          onAddExisting={() => setShowAddExisting(true)}
          onAddFacilitator={isAdmin ? () => setShowAddFacilitator(true) : undefined}
          onRemoveFacilitator={isAdmin ? (m) => setRemovingFacilitator(m) : undefined}
          onRemoveMember={(m) => setRemovingMember(m)}
          nextEvent={upcomingEvents[0] ?? null}
          forumEvents={forumEvents}
        />
      )}

      <ConfirmModal
        isOpen={skippingOccurrence !== null}
        onConfirm={handleSkipOccurrence}
        onCancel={() => setSkippingOccurrence(null)}
        title="Cancel this meeting?"
        message={
          skippingOccurrence
            ? `Only the ${new Date(skippingOccurrence.starts_at).toLocaleDateString(undefined, {
                month: 'long',
                day: 'numeric',
              })} meeting will be cancelled. Your regular schedule is unchanged.`
            : ''
        }
        confirmLabel="Cancel meeting"
        variant="danger"
      />

      {showScheduleScope && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-scope-title"
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="schedule-scope-title" className="text-xl font-semibold text-navy">
                  Edit Schedule
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Choose whether this change is for one generated meeting or the recurring schedule.
                </p>
              </div>
              <button
                onClick={() => setShowScheduleScope(false)}
                aria-label="Close schedule editor"
                className="p-2 text-gray-400 hover:text-navy rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            {!scheduleScope && (
              <div className="grid sm:grid-cols-2 gap-3 mt-6">
                <button
                  onClick={() => setScheduleScope('temporary')}
                  className="text-left border border-gray-200 hover:border-gold rounded-xl p-4 transition"
                >
                  <span className="block font-semibold text-navy">This month only</span>
                  <span className="block text-sm text-gray-500 mt-1">
                    Pick a current or upcoming occurrence. The regular schedule will stay unchanged.
                  </span>
                </button>
                <button
                  onClick={() => {
                    setScheduleScope('recurring');
                    if (
                      window.confirm(
                        'Change the recurring schedule? Future generated meetings will follow the new pattern. Existing one-month overrides will be preserved.',
                      )
                    ) {
                      setShowScheduleScope(false);
                      openEditForum();
                    } else {
                      setScheduleScope(null);
                    }
                  }}
                  className="text-left border border-gray-200 hover:border-gold rounded-xl p-4 transition"
                >
                  <span className="block font-semibold text-navy">
                    Change recurring schedule
                  </span>
                  <span className="block text-sm text-gray-500 mt-1">
                    Update the Forum's meeting day, week, time, or timezone for future meetings.
                  </span>
                </button>
              </div>
            )}

            {scheduleScope === 'temporary' && (
              <div className="mt-6">
                <button
                  onClick={() => {
                    setScheduleScope(null);
                    setSelectedOccurrenceId(null);
                  }}
                  className="text-sm text-navy font-medium mb-4"
                >
                  ← Change scope
                </button>
                <h3 className="font-medium text-navy">Choose a meeting</h3>
                <p className="text-sm text-gray-500 mt-1 mb-3">
                  Only current and next month's generated meetings are available here.
                </p>
                {scheduleOccurrences.length ? (
                  <div className="space-y-2">
                    {scheduleOccurrences.map((event) => (
                      <button
                        key={event.occurrence_id}
                        onClick={() => setSelectedOccurrenceId(event.occurrence_id)}
                        className={`w-full text-left rounded-lg border p-3 transition ${
                          selectedOccurrenceId === event.occurrence_id
                            ? 'border-gold bg-gold/5'
                            : 'border-gray-100 hover:border-gray-300'
                        }`}
                      >
                        <span className="block font-medium text-navy">{event.title}</span>
                        <span className="block text-sm text-gray-500">
                          {new Date(event.starts_at).toLocaleString(undefined, {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-5">
                    No generated meeting is available this month or next month.
                  </p>
                )}
                {selectedOccurrenceId &&
                  (() => {
                    const event = scheduleOccurrences.find(
                      (item) => item.occurrence_id === selectedOccurrenceId,
                    );
                    return event ? (
                      <OccurrenceEditForm
                        event={event}
                        onSaved={() => {
                          setShowScheduleScope(false);
                          setScheduleScope(null);
                          setSelectedOccurrenceId(null);
                          if (selectedForumId) loadForum(selectedForumId);
                        }}
                        onCancel={() => setSelectedOccurrenceId(null)}
                      />
                    ) : null;
                  })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members/advisors browse their forum's people here; facilitators get
          the richer Forum Roster table in the dashboard panel above instead. */}
      {!canManage && roster && (
        <>
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-navy mb-4">
              Facilitators ({roster.facilitators.length})
            </h2>
            {roster.facilitators.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {roster.facilitators.map((m) => (
                  <MemberCard key={m.id} member={m} interactive={!isGuest} />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No facilitators assigned.</p>
            )}
          </div>

          {!isGuest && (
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-navy mb-4">
              Members ({roster.members.length})
            </h2>
            {roster.members.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {roster.members.map((m) => (
                  <MemberCard key={m.id} member={m} />
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No members yet.</p>
            )}
          </div>
          )}
        </>
      )}

      <MessageForumModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        forumName={roster?.forum.name ?? 'this Forum'}
        threadId={roster?.default_thread_id ?? null}
        canAnnounce={canManage}
      />

      {/* Create / Edit forum details + meeting schedule (single source of
          truth: saving the meeting fields also updates the events calendar). */}
      <ForumFormModal
        isOpen={showForumModal}
        mode={forumModalMode}
        forum={forumModalMode === 'edit' ? forumDetail : null}
        facilitatorCandidates={facilitatorCandidates}
        onClose={() => setShowForumModal(false)}
        onSaved={handleForumSaved}
      />

      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        isAdmin={isAdmin}
        isFacilitator={isFacilitatorRole(user?.role)}
        presetForumId={selectedForumId}
        onInvited={(message) => {
          toast.success(message);
          if (selectedForumId) loadForum(selectedForumId);
        }}
      />

      {/* Add an existing member from the directory */}
      {showAddExisting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add member to {roster?.forum.name}</h3>
              <button
                onClick={() => { setShowAddExisting(false); setMemberSearch(''); }}
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search members..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              />
            </div>
            <div className="flex-1 overflow-y-auto space-y-2">
              {directory
                .filter(
                  (u) =>
                    u.full_name.toLowerCase().includes(memberSearch.toLowerCase()) &&
                    !roster?.members.some((m) => m.id === u.id) &&
                    !roster?.facilitators.some((f) => f.id === u.id),
                )
                .map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleAddExisting(u.id)}
                    className="w-full flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-semibold">
                      {u.full_name.split(' ').map((n) => n[0]).join('')}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{u.full_name}</div>
                      <div className="text-sm text-gray-500">{u.role}</div>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Assign a facilitator (admin only) */}
      {showAddFacilitator && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add facilitator to {roster?.forum.name}</h3>
              <button
                onClick={() => { setShowAddFacilitator(false); setMemberSearch(''); }}
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold"
              />
            </div>
            <div className="flex-1 overflow-y-auto space-y-2">
              {directory
                .filter(
                  (u) =>
                    u.full_name.toLowerCase().includes(memberSearch.toLowerCase()) &&
                    !roster?.facilitators.some((f) => f.id === u.id),
                )
                .map((u) => (
                  <button
                    key={u.id}
                    onClick={() => handleAddFacilitator(u.id)}
                    className="w-full flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition text-left"
                  >
                    <div className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center text-navy font-semibold">
                      {u.full_name.split(' ').map((n) => n[0]).join('')}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{u.full_name}</div>
                      <div className="text-sm text-gray-500">{u.role}</div>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={removingMember !== null}
        onConfirm={handleRemoveMember}
        onCancel={() => setRemovingMember(null)}
        title="Remove member?"
        message={
          removingMember
            ? `Remove ${removingMember.full_name} from ${roster?.forum.name ?? 'this Forum'}? Their account stays active — they just leave this Forum.`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
      />

      <ConfirmModal
        isOpen={removingFacilitator !== null}
        onConfirm={handleRemoveFacilitator}
        onCancel={() => setRemovingFacilitator(null)}
        title="Remove facilitator?"
        message={
          removingFacilitator
            ? `Remove ${removingFacilitator.full_name} as a facilitator of ${roster?.forum.name ?? 'this Forum'}?`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
}

// Reschedule ONE occurrence of the recurring schedule (or edit a one-off).
// Saves a concrete override Event for that date; the pattern is untouched, so
// next month reverts to the regular time automatically.
function OccurrenceEditForm({
  event,
  onSaved,
  onCancel,
}: {
  event: UpcomingEvent;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const starts = new Date(event.starts_at);
  const ends = new Date(event.ends_at);
  const pad = (n: number) => String(n).padStart(2, '0');
  const [date, setDate] = useState(
    `${starts.getFullYear()}-${pad(starts.getMonth() + 1)}-${pad(starts.getDate())}`,
  );
  const [time, setTime] = useState(`${pad(starts.getHours())}:${pad(starts.getMinutes())}`);
  const [duration, setDuration] = useState(
    Math.max(15, Math.round((ends.getTime() - starts.getTime()) / 60000)),
  );
  const [location, setLocation] = useState(event.location);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const newStart = new Date(`${date}T${time}`);
    if (Number.isNaN(newStart.getTime())) {
      toast.error('Please choose a valid date and time');
      return;
    }
    const newEnd = new Date(newStart.getTime() + duration * 60000);
    setSaving(true);
    try {
      const fields = {
        title: event.title,
        starts_at: newStart.toISOString(),
        ends_at: newEnd.toISOString(),
        location,
        description: event.description,
      };
      if (event.id) {
        await eventsAPI.updateEvent(event.id, fields);
      } else {
        await eventsAPI.createEvent({
          forum_id: event.forum,
          schedule: event.schedule,
          is_override: true,
          occurrence_date: event.occurrence_date,
          status: 'scheduled',
          ...fields,
        });
      }
      toast.success('Meeting updated — the regular schedule is unchanged');
      onSaved();
    } catch {
      toast.error('Could not update this meeting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 p-4 bg-white rounded-lg border border-gray-100 space-y-3">
      <p className="text-xs text-gray-500">
        Changes apply to this meeting only. Times are in your local timezone.
      </p>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg"
          />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Start time</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg"
          />
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 mb-1">Duration (min)</span>
          <input
            type="number"
            min={15}
            step={15}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg"
          />
        </label>
        <label className="text-sm sm:col-span-3">
          <span className="block text-gray-600 mb-1">Location (optional)</span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Same as usual"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg"
          />
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 transition"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save this meeting'}
        </button>
      </div>
    </div>
  );
}
